import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';

import { container } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
        maxWidth: 800
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginLeft: {
        marginLeft: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    error: {
        color: theme.palette.error.main,
        fontSize: 12,
        display: 'block'
    },
    list: {
        backgroundColor: theme.palette.grey[200],
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        overflowY: 'auto'
    },
    listItem: {
        backgroundColor: '#ffffff',
        marginBottom: theme.spacing(1),
    },
}));

export default function EventReview(props) {

    const {
        event,
        tickets,
        isError,
        getErrorMessage,
        eventNameWithDate,
        advancedOpen
    } = props;

    const classes = useStyles();

    const getValue = value => {
        if (!value || value === null || value === "") return 'n/a';
        return value;
    }

    return (
        <div className={classes.container}>
            <Alert severity="info" className={classes.section}>
                Please review your event details then click <strong>PUBLISH NOW</strong> to submit your event, or <strong>SAVE AS DRAFT</strong> to save for editing later.
            </Alert>
            <div className={classes.section}>
                <Typography variant="h2" component="h4">
                    {event.name}
                    {isError('name') &&
                        <span className={classes.error}>
                            {getErrorMessage('name')}
                        </span>
                    }
                </Typography>
            </div>
            <div className={classes.section}>
                <Typography variant="h6" className={classes.marginBottom}>
                    Event Details
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            Short Discription:
                            {isError('description') &&
                                <span className={classes.error}>
                                   {getErrorMessage('short_description')}
                                </span>
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {getValue(event.short_description)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            Start Date:
                            {isError('starts_at') &&
                                <span className={classes.error}>
                                   {getErrorMessage('starts_at')}
                                </span>
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {event.starts_at.toLocaleString(DateTime.DATETIME_FULL)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            End Time:
                            {isError('ends_at') &&
                                <span className={classes.error}>
                                   {getErrorMessage('ends_at')}
                                </span>
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {event.ends_at.toLocaleString(DateTime.TIME_SIMPLE)}
                        </Typography>
                    </Grid>
                </Grid>
                { event.repeated && (
                    <Grid container spacing={3}>
                        <Grid item xs={6}>
                            <Typography color="textSecondary" variant="subtitle2">
                                Event Will Cycle Every:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">
                                {event.repeat_amount.toString() + " " + event.repeat_period}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography color="textSecondary" variant="subtitle2">
                                Event will appear as:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" color="primary">
                                {eventNameWithDate}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </div>
            <Divider className={classes.section}/>
            <div className={classes.section}>
                <Typography variant="h6" className={classes.marginBottom}>
                    Guest List Details
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Typography color="textSecondary" variant="subtitle2">
                            RSVP?
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {event.rsvp ? "Yes" : "No"}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color='textSecondary' variant="subtitle2">
                            Add Spouse Names to Invitation?
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {event.addSpouse ? "Yes" : "No"}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color='textSecondary' variant="subtitle2">
                            Email Invitations Now?
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2">
                            {event.emailInvitations ? "Yes" : "No"}
                        </Typography>
                    </Grid>
                    {(advancedOpen) &&
                        <>
                            <Grid item xs={6}>
                                <Typography color='textSecondary' variant="subtitle2">
                                    Default Invitation Title
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">
                                    {event.invitation_title == '' ? `You have been invited to ${event.name}!` : event.invitation_title}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography color='textSecondary' variant="subtitle2">
                                    Dates Allowed per Invitation:
                                    {isError('dates_allowed') &&
                                        <span className={classes.error}>
                                            {getErrorMessage('dates_allowed')}
                                        </span>
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">
                                    {event.dates_allowed}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography color='textSecondary' variant="subtitle2">
                                    Flag Guests with Outstanding Invoices?
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">
                                    {event.flag_outstanding ? "Yes" : "No"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography color='textSecondary' variant="subtitle2">
                                    Are group members required to have tickets?
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">
                                {event.group_tickets_required ? "Yes" : "No"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography color='textSecondary' variant="subtitle2">
                                    Can group members send out invitations to this event?
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2">
                                    {event.invitations_active ? "Yes" : "No"}
                                </Typography>
                            </Grid>
                            {event.invitations_active &&
                                <>
                                    <Grid item xs={6}>
                                        <Typography color='textSecondary' variant="subtitle2">
                                            Are invited guests required to have a ticket?
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2">
                                            {event.guest_tickets_required ? "Yes" : "No"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography color='textSecondary' variant="subtitle2">
                                            Are users required to select a ticket for their invited guest?
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2">
                                            {event.force_guest_ticket ? "Yes" : "No"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography color='textSecondary' variant="subtitle2">
                                            Guest Invitations Allowed per Group Member:
                                            {isError('guests_allowed') &&
                                                <span className={classes.error}>
                                                    {getErrorMessage('guests_allowed')}
                                                </span>
                                            }
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2">
                                            {event.guests_allowed}
                                        </Typography>
                                    </Grid>
                                </>
                            }
                        </>
                    }
                </Grid>
            </div>
            <Divider className={classes.section}/>
            <div className={classes.section}>
                <Typography variant="h6" gutterBottom>
                    Tickets
                    <Chip
                        className={classes.marginLeft}
                        label={event.group_tickets_required ? 'Required for Group Members' : 'Not Required for Group Members'}
                        color={event.group_tickets_required ? 'primary' : 'default'}
                    />
                    {event.invitations_active &&
                        <Chip
                            className={classes.marginLeft}
                            label={event.guest_tickets_required ? 'Required for Invited Guests' : 'Not Required for Invited Guests'}
                            color={event.guest_tickets_required ? 'primary' : 'default'}
                        />
                    }
                </Typography>
                {tickets.length ? (
                    <div className={classes.marginTop}>
                        {tickets.map((ticket, index) => (
                            <List key={index} className={classes.list}>
                                <ListItem className={classes.listItem}>
                                    <Grid container direction="column">
                                        <Grid item>
                                            <Typography variant="h6">
                                                {ticket.title}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body1">
                                                {ticket.description}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Grid container direction="row" justify="space-between">
                                                <Grid item>
                                                    Price Per Ticket: <strong>${ticket.price}</strong>
                                                </Grid>
                                                <Grid item>
                                                    Number of Tickets: <strong>{ticket.amount}</strong>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {ticket.late_by &&
                                            <Grid item style={{marginTop: 10}}>
                                                <Grid container direction="row" justify="space-between">
                                                    <Grid item >
                                                        Late Purchase Price:  <strong>${ticket.late_price}</strong>
                                                    </Grid>
                                                    <Grid item>
                                                        Late Purchase Date:  <strong>{ticket.late_by.toLocaleString(DateTime.DATETIME_SMALL)}</strong>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        }
                                    </Grid>
                                </ListItem>
                            </List>
                        ))}
                    </div>
                ) : (
                    <Alert severity="warning" className={classes.marginTop}>
                        No ticket types have been created for this event.
                    </Alert>
                )}
            </div>
        </div>
    )
}
